import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { SidebarLink, theme, UserAvatar } from '@side/pantry';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAgentTeamId } from '@/utils/use-agent-team-id';
import { AnalyticsEvent, useTrackEvent } from '$analytics';
import { useAppFlags } from '$launchdarkly/useAppFlags';
import { UploadImageModal } from '../UploadImageModal';
import {
  ACCOUNT_NAVIGATION_LINKS,
  AccountNavigationLabel,
} from './accountNavigation';
import { useAccountSettingsLogic } from './useAccountSettingsLogic';
import { ViewTeamSelector } from './ViewTeamSelector';

export const AccountSettingsSideNavigation = () => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const trackEvent = useTrackEvent();
  const { identityUpdateRoles } = useAppFlags();

  const { agentTeamId, isLoading: agentTeamIdLoading } = useAgentTeamId();

  const teamIdParam = searchParams?.get('team');
  const teamId = typeof teamIdParam === 'string' ? teamIdParam : agentTeamId;

  const {
    user,
    isLoading: userinfoLoading,
    name,
    teams,
    avatarEditModalOpen,
    setAvatarEditModalOpen,
    loadingUpdateAvatar,
    handleTeamChange,
    onSubmitAvatar,
  } = useAccountSettingsLogic();

  // Gate on both requests: userinfo drives the avatar/name, and agentTeamId
  // comes from a second (team-members) call that only starts once userinfo
  // returns. Coalescing avoids a flash where the skeletons clear but the team
  // selector renders with no selection yet.
  const userLoading = userinfoLoading || agentTeamIdLoading;

  const fullCurrentPath = `${process.env.NEXT_PUBLIC_BASE_PATH ?? ''}${pathname ?? ''}`;

  return (
    <Stack
      sx={{
        justifyContent: 'space-between',
        gap: 3,
        width: 240,
        borderRight: '1px solid',
        borderColor: 'other.outlineBorder',
        backgroundColor: 'background.paper',
      }}
    >
      <Stack
        sx={{
          gap: 3,
          p: 2,
          height: '100%',
        }}
      >
        <Stack sx={{ gap: 2 }}>
          <Box
            sx={{
              position: 'relative',
              width: theme.spacing(11),
              height: theme.spacing(11),
            }}
          >
            {userLoading ? (
              <>
                <Skeleton
                  variant="circular"
                  sx={{ height: '100%', width: '100%' }}
                />
                <Box
                  aria-label="Edit avatar"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: theme.spacing(3.5),
                    height: theme.spacing(3.5),
                    backgroundColor: 'action.disabledBackground',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'action.disabled',
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </Box>
              </>
            ) : (
              <>
                <UserAvatar
                  name={name}
                  avatarUrl={user?.photoUrl || ''}
                  sx={{
                    height: '100%',
                    width: '100%',
                  }}
                />
                <IconButton
                  aria-label="Edit avatar"
                  onClick={() => setAvatarEditModalOpen(true)}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: 'primary.main',
                    color: 'common.white',
                    width: theme.spacing(3.5),
                    height: theme.spacing(3.5),
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: theme.spacing(2) }} />
                </IconButton>
              </>
            )}
          </Box>

          {userLoading ? (
            <Skeleton variant="rectangular" width={150} height={19} />
          ) : (
            <Typography variant="subtitle2" component="h1" noWrap>
              {name}
            </Typography>
          )}

          {!identityUpdateRoles &&
            (userLoading ? (
              <Skeleton variant="rectangular" width={208} height={19} />
            ) : (
              <ViewTeamSelector
                teams={teams}
                teamId={teamId}
                handleTeamChange={(event) =>
                  handleTeamChange(event.target.value)
                }
              />
            ))}

          <Divider />
        </Stack>

        <Box>
          {ACCOUNT_NAVIGATION_LINKS.map((link) => {
            const isActive = fullCurrentPath === link.pathname;

            return (
              <SidebarLink
                key={link.pathname}
                label={link.label}
                pathname={link.pathname}
                linkComponent={Link}
                basePath={process.env.NEXT_PUBLIC_BASE_PATH ?? ''}
                currentPath={pathname ?? ''}
                active={isActive}
                onClick={() => {
                  switch (link.label) {
                    case AccountNavigationLabel.AccountProfile:
                      trackEvent(AnalyticsEvent.Sidebar, {
                        eventTarget: 'account_profile',
                      });
                      break;
                    case AccountNavigationLabel.TeamManagement:
                      trackEvent(AnalyticsEvent.Sidebar, {
                        eventTarget: 'team_management',
                      });
                      break;
                    case AccountNavigationLabel.Notifications:
                      trackEvent(AnalyticsEvent.Sidebar, {
                        eventTarget: 'notifications',
                      });
                      break;
                    default:
                      break;
                  }
                }}
              />
            );
          })}
        </Box>
      </Stack>

      <UploadImageModal
        key={user?.photoUrl || 'new'}
        title="Select a photo"
        headline={
          name ? (
            <>
              Upload a new photo for <b>{name}</b>
            </>
          ) : (
            `Upload a new photo`
          )
        }
        open={avatarEditModalOpen}
        onClose={() => setAvatarEditModalOpen(false)}
        onSubmit={onSubmitAvatar}
        uploading={loadingUpdateAvatar}
        imageUrl={user?.photoUrl}
      />
    </Stack>
  );
};
